import React from "react";
import { ErrorMessage, RadioButton, FormFieldRadioButtonGroup } from "@vwfs-bronson/bronson-react";
import { useField } from "formik";
import { Label } from "./Label";

interface Props {
  formField: any;
  label?: string;
  options: { [value: string]: string };
  infoIconText?: React.ReactNode;
  onSelectionChange?: (newValue: string, oldValue: string) => void;
  required?: boolean;
}

export const RadioButtonGroup = ({ formField, label, options, infoIconText, onSelectionChange, required = false }: Props) => {
  const { fieldName } = formField;
  const [{ value, onBlur }, { touched, error }, { setValue }] = useField<string>(fieldName);

  const errorMessage = touched && error ? <ErrorMessage id={fieldName}>{error}</ErrorMessage> : null;

  return (
    <>
      {label && <Label id={fieldName} text={label} infoIconText={infoIconText} />}
      <FormFieldRadioButtonGroup errorMessage={!!errorMessage} required={required} aria-labelledby={fieldName}>
        {Object.keys(options).map((optionValue) => {
          return (
            <RadioButton
              key={`${fieldName}.${optionValue}`}
              name={fieldName}
              onBlur={onBlur}
              checked={optionValue === value}
              onChange={(event: any) => {
                const oldValue = value;
                const newValue = event.target.value;
                setValue(newValue);
                if (newValue !== oldValue) {
                  onSelectionChange?.(newValue, oldValue);
                }
              }}
              value={optionValue}
              aria-describedby={`error-${fieldName}`}
            >
              {options[optionValue]}
            </RadioButton>
          );
        })}
      </FormFieldRadioButtonGroup>
      {errorMessage}
    </>
  );
};

const YES_NO_OPTIONS = { true: "Ja", false: "Nein" };
interface BooleanProps {
  formField: any;
  label?: string;
  options: { [value: string]: string };
  disabled?: string[];
  infoIconText?: React.ReactNode;
  onSelectionChange?: (newValue: boolean, oldValue: boolean) => void;
  required?: boolean;
}

export const BooleanRadioButtonGroup = ({ formField, label, options, infoIconText, onSelectionChange, disabled = [], required = false }: BooleanProps) => {
  const { fieldName } = formField;
  const [{ value, onBlur }, { touched, error }, { setValue }] = useField<boolean>(fieldName);

  const errorMessage = touched && error ? <ErrorMessage id={fieldName}>{error}</ErrorMessage> : null;

  return (
    <>
      {label && <Label id={fieldName} text={label} infoIconText={infoIconText} />}
      <FormFieldRadioButtonGroup errorMessage={!!errorMessage} required={required} aria-labelledby={fieldName}>
        {Object.keys(options).map((optionValue) => {
          return (
            <RadioButton
              key={`${fieldName}.${optionValue}`}
              error={!!errorMessage}
              name={fieldName}
              onBlur={onBlur}
              checked={(optionValue === "true") === value}
              disabled={disabled?.includes(optionValue)}
              onChange={(event: any) => {
                const oldValue = value;
                const newValue = event.target.value === "true";
                setValue(newValue);
                if (newValue !== oldValue) {
                  onSelectionChange?.(newValue, oldValue);
                }
              }}
              aria-describedby={`error-${fieldName}`}
              /* @ts-ignore */
              value={optionValue === "true"}
            >
              {options[optionValue]}
            </RadioButton>
          );
        })}
      </FormFieldRadioButtonGroup>
      {errorMessage}
    </>
  );
};

BooleanRadioButtonGroup.defaultProps = {
  options: YES_NO_OPTIONS,
};
